import React from 'react';
import { useTranslation } from 'react-i18next';
import { CustomCheckbox } from './CustomCheckbox';
import CustomChip from './CustomChip';
import ImagePreview from './ImagePreview';
import { IconCheckboxChecked, IconCheckboxUnchecked } from './buttons/IconButtons';

/**
 *
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 * * @Description component CustomCardGrid.
 * @param Array<JSX.Element> children: List of JSX.Element (required).
 * @param string itemId: Object Id (required).
 * @param string checkboxId: Optional id for the checkbox (optional, default: '').
 * @param boolean checked: Boolean value to define the checkbox state (required).
 * @param function onCheckboxChange: Event to handle checkbox state change (required).
 * @param boolean disabled: Boolean value to disable the checkbox (optional, default: false).
 * @param string statusName: Label text for the chip component (required).
 * @param string statusColor: Background color for the status chip (optional).
 * @param string srcImage: Image source URL (required if you want to show an image).
 * @param string backgroundImage: Background card.
 * @param JSX.Element btnActions: Element with action buttons shown below the content (optional).
 * @param string classNameActions: CSS classes for the action buttons container (optional, default: '').
 * @param string classNamePanelGrid: CSS classes for the outer container (optional, default: standard responsive grid classes).
 * @param boolean isCheckboxShow: Flag to show or hide the checkbox (optional, default: true).
 * @param boolean showCustomChip: Flag to show or hide the status chip (optional, default: true).
 * @param boolean isSquareImg: Flag to show a full-width square image instead of the small preview (optional, default: false).
 * @param string height: CSS height utility class for the card (optional, default: 'h-100').
 * @param string fontColor: Font color for the status chip text (optional, default: '#FFFFFF').
 * @param string borderColor: Border color for the status chip (optional).
 * @param function onClick: Function to handle the click event on the card (optional, default: no-op).
 * @param {boolean} [props.isDefaultIcon=false] - Whether to apply `object-fit-fill` to the image.
 * @param {string} [imgContainerClass='h-100'] - CSS class for the image container.
 * @param {string} [headClass='minh-200px'] - CSS class for the top header/image container.
 * @returns {JSX.Element}
 */
export const CustomCardGrid = ({
    children,
    itemId,
    checkboxId = '',
    checked,
    onCheckboxChange,
    disabled = false,
    statusName,
    isCheckboxShow = true,
    statusColor,
    srcImage,
    backgroundImage,
    btnActions,
    classNameActions = '',
    classNamePanelGrid = 'col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 px-1 mb-3',
    height = 'h-100',
    showCustomChip = true,
    isSquareImg = false,
    fontColor = '#FFFFFF',
    borderColor,
    onClick = () => { },
    isDefaultIcon = false,
    imgContainerClass="h-100",
    headClass="minh-200px",
    imgWidth="h-100",
    chipVariant
}) => {
    const { t } = useTranslation();

    const getImageElement = () => {
        if (!srcImage) return null;

        const classes = "image-full-container position-absolute"

        // If it is already a React element
        if (React.isValidElement(srcImage)) {
            return <div className={imgContainerClass}>{React.cloneElement(srcImage, {
                className: `${ classes } ${ imgWidth } object-fit-fill`
            })}</div>
        }

        // If it is string (URL, base64, etc.)
        if (typeof srcImage === 'string') {
            return (
                <img
                    src={srcImage}
                    alt="preview"
                    className={`${classes} ${isDefaultIcon ? "object-fit-fill" : ""} ${imgWidth}`}
                />
            );
        }

        return null;
    };


    return (
        <>
            <div className={`panel-grid ${classNamePanelGrid}`} key={itemId} onClick={onClick}>
                <div className={`card-base-grid ${height}`} key={itemId}>
                    <div className={`top-hearing-container position-relative ${headClass}`}>
                        {!isSquareImg && backgroundImage && (
                            <>
                                <img
                                    src={backgroundImage}
                                    alt='background'
                                    className='w-100 h-100 position-absolute top-0 start-0 object-fit-cover'
                                />
                                <div className='glass-overlay w-100 h-100 position-absolute top-0 start-0' />
                            </>
                        )}
                        {isSquareImg && srcImage && getImageElement()}

                        {(isCheckboxShow || showCustomChip) && (
                            <div className='top-heading-card position-relative z-1'>
                                {isCheckboxShow && (
                                    <CustomCheckbox
                                        id={checkboxId}
                                        fontSize='10px'
                                        name='selectCard'
                                        checked={checked}
                                        disabled={disabled}
                                        onChange={(e) => {
                                            onCheckboxChange({
                                                target: {
                                                    id: itemId,
                                                    checked: e.target.checked,
                                                    value: e.target?.value,
                                                },
                                            });
                                        }}
                                        icon={<IconCheckboxUnchecked className={"icon-color-general-950"}/>}
                                        checkedIcon={<IconCheckboxChecked className={"icon-color-shades-800"}/>}
                                        disableRipple={true}
                                    />
                                )}

                                {showCustomChip && (
                                    <div className='ms-auto'>
                                        <CustomChip
                                            background={statusColor}
                                            legend={t(statusName) || '-'}
                                            fontColor={fontColor}
                                            borderColor={borderColor}
                                            variant={chipVariant}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {!isSquareImg && srcImage && (
                        <div className='d-flex justify-content-center mt-n5'>
                            <ImagePreview preview={srcImage} classNameWH={`w-100px-h-100px`} />
                        </div>
                    )}
                    <div className='card-body-grid mt-3 px-2 pb-2'>
                        <div> {children}</div>
                        {btnActions && <div className={`card-detail-grid  ${classNameActions}`}>{btnActions}</div>}
                    </div>
                </div>
            </div>
        </>
    );
};
